import React, { useState } from 'react';
import PageHero from './PageHero';
import {
  MessageSquare,
  Compass,
  FileText,
  UserCheck,
  Award,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  Globe2,
  BookOpen,
  Info,
  GraduationCap,
  Calculator,
  Layers
} from 'lucide-react';
import heroImg from '../assets/Banner-GMIS Students.png';

export default function AdmissionsProcessPage({ setPage, t }) {
  // Localization source from t with default fallbacks
  const content = t?.admissionsProcess || {};
  const ygs = content?.yearGroupSection || {};
  const gl = content?.guidelines || {};
  const cta = content?.cta || {};

  const [selectedAge, setSelectedAge] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');

  const placements = ygs?.placements || [
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Toddler',
      age: '2–3',
      curriculum: 'Early Childhood Sensory & Play Inquiry',
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Nursery',
      age: '3–4',
      curriculum: 'Foundational Literacy & Social Development',
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Kindergarten',
      age: '4–5',
      curriculum: 'Early Numeracy, Phonics & Creative Arts',
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Preparatory',
      age: '5–6',
      curriculum: 'Transition to Primary Structured Learning',
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Primary School',
      stageKey: 'primary',
      group: 'Grade 1 – 5',
      age: '6–11',
      curriculum: 'IB Primary Years Programme (PYP) & Transdisciplinary Themes',
      badgeColor: 'bg-blue-500/10 text-blue-700 border-blue-200'
    },
    {
      stage: 'Middle School',
      stageKey: 'middle',
      group: 'Grade 6 – 8',
      age: '11–14',
      curriculum: 'IB Middle Years Programme (MYP) & Critical Inquiry Projects',
      badgeColor: 'bg-emerald-500/10 text-emerald-700 border-emerald-200'
    },
    {
      stage: 'Senior School',
      stageKey: 'senior',
      group: 'Grade 9 – 10',
      age: '14–16',
      curriculum: 'Cambridge IGCSE & IB MYP Culminating Pathways',
      badgeColor: 'bg-indigo-500/10 text-indigo-700 border-indigo-200'
    },
    {
      stage: 'Senior School',
      stageKey: 'senior',
      group: 'Grade 11 – 12',
      age: '16–18',
      curriculum: 'IB Diploma Programme (DP) / Career (CP) / Cambridge A-Levels',
      badgeColor: 'bg-purple-500/10 text-purple-700 border-purple-200'
    }
  ];

  const stepsList = content?.steps || [
    {
      num: '01',
      title: 'Enquire',
      desc: 'Get in touch with our Admissions Team to learn more about GMIS, our programmes and the pathway that best suits your child.'
    },
    {
      num: '02',
      title: 'School Visit',
      desc: 'Visit GMIS Jakarta to experience our learning environment, explore the school and speak with our Admissions Team.'
    },
    {
      num: '03',
      title: 'Submit Your Application',
      desc: 'Complete the application and provide the required documents for your child’s admission.'
    },
    {
      num: '04',
      title: 'Assessment & Interview',
      desc: 'Depending on the programme and grade, students may be invited to complete an assessment and/or attend an interview as part of the admission process.'
    },
    {
      num: '05',
      title: 'Admission Decision',
      desc: 'Following the assessment and review of the application, the school will communicate the admission decision and next steps.'
    },
    {
      num: '06',
      title: 'Enrolment',
      desc: 'Once your child has been accepted, complete the enrolment process and prepare for their journey at GMIS Jakarta.'
    }
  ];

  const stepIcons = [
    <MessageSquare className="w-6 h-6" />,
    <Compass className="w-6 h-6" />,
    <FileText className="w-6 h-6" />,
    <UserCheck className="w-6 h-6" />,
    <Award className="w-6 h-6" />,
    <Sparkles className="w-6 h-6" />
  ];

  const filteredPlacements = placements.filter(item => {
    if (stageFilter === 'all') return true;
    return item.stageKey === stageFilter;
  });

  const isAgeMatch = (ageStr) => {
    if (!selectedAge || !ageStr) return false;
    const parts = ageStr.split(/[–-]/);
    if (parts.length >= 2) {
      const min = parseInt(parts[0].trim(), 10);
      const max = parseInt(parts[1].trim(), 10);
      return selectedAge >= min && selectedAge <= max;
    }
    return false;
  };

  const handleNavigateToContact = () => {
    if (setPage) setPage('contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateToFaqs = () => {
    if (setPage) setPage('faqs');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Page Hero ────────────────────────────────────────────── */}
      <PageHero
        title={content.title || 'A Simple Journey to GMIS Jakarta'}
        eyebrow={content.eyebrow || 'ADMISSIONS PROCESS'}
        subtitle={content.subtitle || 'Choosing a school is an important decision. Our Admissions Team is here to guide families through each step, from the initial enquiry to enrolment.'}
        breadcrumbs={content.breadcrumbs || ['Home', 'Admissions', 'Admissions Process']}
        bgImage={heroImg}
      />

      {/* ── Intro Floating Action Bar ──────────────────────────────────── */}
      <section className="relative -mt-10 z-20 max-w-[1240px] mx-auto px-6 box-border">
        <div className="bg-white rounded-2xl p-5 lg:p-7 shadow-xl border border-line flex flex-col lg:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4 w-full lg:w-auto">
            <div className="w-13 h-13 rounded-2xl bg-gold/15 flex items-center justify-center text-gold-ink shrink-0">
              <GraduationCap className="w-7 h-7 text-gold-ink" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-[0.2em] text-gold uppercase mb-1">
                {content.introBadge || 'Personalized Admissions Journey'}
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-navy m-0">
                {content.introTitle || 'Your Partner in Educational Excellence'}
              </h2>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <button
              onClick={handleNavigateToContact}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-gold text-navy-deep font-heading font-bold text-[13px] tracking-wider uppercase transition-all shadow-md hover:shadow-lg"
            >
              <span>{content.floatingContact || 'Contact Admissions'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('year-groups')}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-navy/20 bg-paper text-navy font-heading font-bold text-[13px] tracking-wider uppercase hover:bg-white transition-all"
            >
              <Calculator className="w-4 h-4 text-gold-ink" />
              <span>{content.floatingPlacement || 'Year Group Placement'}</span>
            </button>
            <button
              onClick={handleNavigateToFaqs}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-navy/20 bg-paper text-navy font-heading font-bold text-[13px] tracking-wider uppercase hover:bg-white transition-all"
            >
              <HelpCircle className="w-4 h-4 text-gold-ink" />
              <span>{content.floatingFaqs || 'Admissions FAQs'}</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Main Container ────────────────────────────────────────────── */}
      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] py-14 lg:py-20 box-border">

        {/* ── Section 1 Header: The 6-Step Admission Journey ── */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
            <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
              {content.journeyEyebrow || 'Step-by-Step Guide'}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
            {content.journeyTitle || 'The 6-Step Admission Journey'}
          </h2>
          <p className="text-[16px] md:text-[17px] text-muted leading-relaxed m-0">
            {content.subtitle || 'Choosing a school is an important decision. Our Admissions Team is here to guide families through each step, from the initial enquiry to enrolment.'}
          </p>
        </div>

        {/* ── 6 Step Cards Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 mb-20">
          {stepsList.map((step, idx) => (
            <div
              key={step.num}
              className="relative bg-white rounded-2xl border border-line shadow-sm hover:border-gold/50 hover:shadow-xl transition-all duration-300 p-6 md:p-7 flex flex-col justify-between group hover:-translate-y-1.5"
            >
              <div>
                {/* Top Bar with Big Number & Icon */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <span className="text-[32px] font-heading font-extrabold text-navy/20 group-hover:text-gold transition-colors tracking-tighter">
                      {step.num}
                    </span>
                    <div className="w-6 h-0.5 bg-gold/50 rounded-full" />
                  </div>

                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 bg-paper text-navy group-hover:bg-gold/15 group-hover:text-gold-ink">
                    {stepIcons[idx]}
                  </div>
                </div>

                {/* Step Title */}
                <h3 className="text-xl font-heading font-bold text-navy group-hover:text-gold-ink transition-colors mb-2.5 leading-snug m-0">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="text-[14.5px] text-ink/80 leading-relaxed mb-6 m-0">
                  {step.desc}
                </p>
              </div>

              {/* Bottom Footer Pill */}
              <div className="pt-3.5 border-t border-line/60 flex items-center justify-between text-[12px] font-medium text-muted">
                <span className="inline-flex items-center gap-1.5 text-navy font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5 text-gold" />
                  <span>{content.guidanceBadge || 'Admissions Guidance'}</span>
                </span>
                <span className="text-gold font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                  {(content.stepLabel || 'Step') + ' ' + step.num} →
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Section 2: Year Group Placement ────────────────────────────── */}
        <div id="year-groups" className="mb-20 scroll-mt-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                {ygs.eyebrow || 'Academic Alignment'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
              {ygs.title || 'Year Group Placement'}
            </h2>
            <p className="text-[16px] md:text-[17px] text-muted leading-relaxed m-0">
              {ygs.subtitle || 'The age ranges below are indicative and should be confirmed with the Academic Team before joining GMIS.'}
            </p>
          </div>

          {/* Interactive Filters & Age Finder */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-line shadow-sm mb-7 flex flex-col lg:flex-row items-center justify-between gap-5">
            {/* Filter buttons */}
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <span className="text-[12px] font-bold uppercase tracking-wider text-muted mr-1 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-gold" /> {ygs.filterLabel || 'Filter Stage:'}
              </span>
              {[
                { id: 'all', label: ygs.filterAll || 'All Stages' },
                { id: 'preschool', label: ygs.stages?.preschool || 'Preschool' },
                { id: 'primary', label: ygs.stages?.primary || 'Primary School' },
                { id: 'middle', label: ygs.stages?.middle || 'Middle School' },
                { id: 'senior', label: ygs.stages?.senior || 'Senior School' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStageFilter(f.id)}
                  className={`px-3.5 py-1.5 rounded-xl text-[13px] font-heading font-semibold transition-all ${stageFilter === f.id
                    ? 'bg-navy text-white shadow-sm'
                    : 'bg-paper text-ink/80 hover:bg-gold/15 hover:text-navy'
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Quick Age Checker */}
            <div className="flex items-center gap-3 w-full lg:w-auto justify-end border-t lg:border-t-0 pt-4 lg:pt-0 border-line/60">
              <span className="text-[13px] font-medium text-muted flex items-center gap-1.5 whitespace-nowrap">
                <Calculator className="w-4 h-4 text-gold" /> {ygs.ageLabel || 'Find by Child’s Age:'}
              </span>
              <select
                value={selectedAge || ''}
                onChange={(e) => setSelectedAge(e.target.value ? parseInt(e.target.value, 10) : null)}
                className="bg-paper border border-line rounded-xl px-3 py-1.5 text-[13.5px] font-semibold text-navy focus:outline-none focus:border-gold"
              >
                <option value="">{ygs.selectAge || 'Select Age'}</option>
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(age => (
                  <option key={age} value={age}>{age} {ygs.yearsOld || 'Years Old'}</option>
                ))}
              </select>
              {selectedAge && (
                <button
                  onClick={() => setSelectedAge(null)}
                  className="text-[11px] font-bold text-muted hover:text-navy uppercase underline"
                >
                  {ygs.clear || 'Clear'}
                </button>
              )}
            </div>
          </div>

          {/* Placement Table */}
          <div className="bg-white rounded-2xl border border-line shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-navy text-white text-[12.5px] font-heading font-bold uppercase tracking-wider border-b border-navy-deep">
                    <th className="py-3.5 px-6 md:px-7 w-[25%]">{ygs.columns?.stage || 'School Stage'}</th>
                    <th className="py-3.5 px-6 md:px-7 w-[25%]">{ygs.columns?.group || 'Year Group'}</th>
                    <th className="py-3.5 px-6 md:px-7 w-[20%] text-center md:text-left">{ygs.columns?.age || 'Typical Age*'}</th>
                    <th className="py-3.5 px-6 md:px-7 w-[30%] hidden lg:table-cell">{ygs.columns?.curriculum || 'Curriculum Focus'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/70 text-[14.5px]">
                  {filteredPlacements.map((item, index) => {
                    const highlighted = isAgeMatch(item.age);
                    return (
                      <tr
                        key={index}
                        className={`transition-colors duration-200 ${highlighted
                          ? 'bg-gold/15 font-semibold text-navy'
                          : index % 2 === 0
                            ? 'bg-white hover:bg-paper/60'
                            : 'bg-[#fcfdfd] hover:bg-paper/60'
                          }`}
                      >
                        {/* School Stage */}
                        <td className="py-3.5 px-6 md:px-7 align-middle">
                          <div className="flex items-center gap-2.5">
                            <span className={`px-3 py-1 rounded-full text-[11.5px] font-bold uppercase tracking-wider border ${item.badgeColor || 'bg-navy/5 text-navy border-line'}`}>
                              {item.stage}
                            </span>
                          </div>
                        </td>

                        {/* Year Group */}
                        <td className="py-3.5 px-6 md:px-7 font-heading font-semibold text-navy align-middle">
                          <div className="flex items-center gap-2">
                            <span>{item.group}</span>
                            {highlighted && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-gold text-navy-deep uppercase tracking-wider">
                                {ygs.match || 'Match'}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Typical Age */}
                        <td className="py-3.5 px-6 md:px-7 font-heading font-bold text-navy-deep text-center md:text-left align-middle">
                          <span className="inline-block px-3 py-1 rounded-lg bg-paper text-navy font-mono text-[14px]">
                            {item.age}
                          </span>
                        </td>

                        {/* Curriculum Focus */}
                        <td className="py-3.5 px-6 md:px-7 text-muted text-[13.5px] hidden lg:table-cell align-middle leading-snug">
                          {item.curriculum}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Table Disclaimer Note */}
            <div className="bg-paper/80 px-6 md:px-7 py-3.5 border-t border-line flex items-center gap-3 text-muted text-[12.5px] italic">
              <Info className="w-4 h-4 text-gold shrink-0" />
              <span>
                {ygs.note || '*Indicative age ranges only. Final placement is subject to review by the Academic Team.'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Section 3: Transfer Guidelines & Pathways ──────────────────── */}
        <div className="mb-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                {gl.eyebrow || 'Admission Pathways'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
              {gl.title || 'Transfer Guidelines & Placement Support'}
            </h2>
            <p className="text-[16px] md:text-[17px] text-muted leading-relaxed m-0">
              {gl.subtitle || 'Whether transferring from an Indonesian national school or relocating from overseas, our team ensures an equitable and smooth academic placement.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-8">
            {/* Card 1: Indonesian Transfers */}
            <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full pointer-events-none" />

              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-red-500/10 text-red-600 flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold tracking-[0.2em] text-red-600 uppercase block">
                      {gl.indonesia?.badge || 'National Transfer'}
                    </span>
                    <h3 className="text-xl font-heading font-bold text-navy m-0">
                      {gl.indonesia?.title || 'For Students Transferring from Schools in Indonesia'}
                    </h3>
                  </div>
                </div>

                <p className="text-[15px] text-ink/80 leading-relaxed mb-5">
                  {gl.indonesia?.desc || 'Students transferring from a school in Indonesia may be asked to provide their most recent school report, commonly referred to as a “rapor”. This helps our Admissions and Academic Teams understand the student’s previous learning experience and determine the most appropriate year group.'}
                </p>

                {/* Highlights */}
                {gl.indonesia?.highlights && (
                  <div className="space-y-2.5 mb-6 bg-paper/60 p-4 rounded-2xl border border-line/60">
                    {gl.indonesia.highlights.map((hl, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                        <span className="text-[13.5px] text-ink/85 font-medium leading-relaxed">
                          {hl}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleNavigateToContact}
                className="inline-flex items-center gap-2 text-navy font-heading font-bold text-[13.5px] hover:text-gold transition-colors pt-1"
              >
                <span>{gl.indonesia?.button || 'Consult on Indonesian Transfers'}</span>
                <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Card 2: International Families */}
            <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-navy/5 rounded-bl-full pointer-events-none" />

              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-navy/10 text-navy flex items-center justify-center shrink-0">
                    <Globe2 className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold tracking-[0.2em] text-gold uppercase block">
                      {gl.international?.badge || 'Global Pathways'}
                    </span>
                    <h3 className="text-xl font-heading font-bold text-navy m-0">
                      {gl.international?.title || 'For International Families'}
                    </h3>
                  </div>
                </div>

                <p className="text-[15px] text-ink/80 leading-relaxed mb-5">
                  {gl.international?.desc || 'Students joining GMIS from overseas may come from different school systems, curricula and year group structures. As these may not always correspond directly with the GMIS structure, placement is reviewed individually to ensure students are placed in the most appropriate year group for their stage of learning.'}
                </p>

                {/* Highlights */}
                {gl.international?.highlights && (
                  <div className="space-y-2.5 mb-6 bg-paper/60 p-4 rounded-2xl border border-line/60">
                    {gl.international.highlights.map((hl, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                        <span className="text-[13.5px] text-ink/85 font-medium leading-relaxed">
                          {hl}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleNavigateToContact}
                className="inline-flex items-center gap-2 text-navy font-heading font-bold text-[13.5px] hover:text-gold transition-colors pt-1"
              >
                <span>{gl.international?.button || 'International Admissions Support'}</span>
                <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Section (PreschoolPage style) ─────────────────────────────── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-8 py-12">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
                {cta.title || 'Not sure which year group is right for your child?'}
              </h3>
              <p className="text-white/70 text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle || 'Our Admissions and Academic Teams are here to guide you with personalized recommendations based on your child’s educational background.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={handleNavigateToContact}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer"
                >
                  {cta.button || 'Speak with our Admissions Team'}
                </button>
                <button
                  onClick={handleNavigateToFaqs}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.faqButton || 'Frequently Asked Questions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
